import { useState, useContext, useEffect, useMemo } from 'react'
import { PassContext } from '../pass-context'
import { flatten } from "flat"
import _ from "lodash"


import { MenuItem, TextField, Typography, Button, Tooltip } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import dataSelector from './data-selector'
import VariableSelectorSelector from '/utils/variable-selector'
import PopupShell from '../popup-shell'
import { validateRequiredVariableValue } from '/utils/variable-required'
import currencies from '/utils/currencies.json'

const PopupDialogText = ({ open, close, title, path, colors, variable }) => {

  const { template, variables, setVariables, setTemplate, openInstanceDataManager, variablesCatalog = [], isManagedPassTemplate = false } = useContext(PassContext)

  const [newTemplate, setNewTemplate] = useState({})
  const [newVariables, setNewVariables] = useState({})
  const [sourcePaths, setSourcePaths] = useState([])
  const catalogMap = useMemo(() => _.keyBy(variablesCatalog, "var_name"), [variablesCatalog])

  const dataChanged = useMemo(() => {
    if (open) {
      if (!_.isEqual(template, newTemplate)) return true
      if (!_.isEqual(variables, newVariables)) return true
    }
    return false
  }, [variables, newVariables, newTemplate, open, template])

  const variablesPath = variable ?? (typeof _.get(newTemplate, path) === "string" ? (_.get(newTemplate, path).match(/^{{(.+)}}$/)?.[1] ?? "") : "")
  const instanceRequiredValidation = useMemo(() => validateRequiredVariableValue({
    value: _.get(newVariables, variablesPath),
    catalogEntry: catalogMap[variablesPath],
    label: "Variable value",
    isManagedPassTemplate
  }), [catalogMap, isManagedPassTemplate, variablesPath, newVariables])

  // Runs on "open" state change or if template / variables change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    if (open) {
      setNewTemplate(_.cloneDeep(template))
      setNewVariables(_.cloneDeep(variables))
    }
  }, [variables, open, path, template])

  // Refresh our template / instance data source paths if instance data changes
  useEffect(() => {
    setSourcePaths(Object.keys(flatten(newVariables)).map(p => `{{${p}}}`).sort())
  }, [newVariables])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {
    if (!_.isEqual(newTemplate, template)) setTemplate(newTemplate)
    if (!_.isEqual(newVariables, variables)) setVariables(newVariables)
    close()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    let item = path.split(".").pop()

    return (
      <PopupShell open={open} onClose={handleClose}>
        <div className="flex flex-col h-full gap-2">

          <div className="flex flex-row justify-between mt-5">
            <Typography color="secondary" variant="h6">{title}</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>
          <div>
            {colors?.[item] && <span className="popup-colour-source-path"><b>colour source :</b> {colors[item].replace("model.", "")}</span>}
          </div>

          <hr />

          <div className='mt-5 overflow-y-scroll flex flex-col gap-5 mb-[100px]'>
            <DataSourceInstance path={path} newTemplate={newTemplate} setNewTemplate={setNewTemplate} variables={newVariables} setNewVariables={setNewVariables} sourcePaths={sourcePaths} catalogMap={catalogMap} isManagedPassTemplate={isManagedPassTemplate} variable={variable} requiredValidation={instanceRequiredValidation} />
          </div>

          <div className='fixed bottom-5 w-[460px]'>
            <hr className='mb-5' />
            <div className="flex flex-row gap-5 justify-between">
              <Button startIcon={<DatasetOutlinedIcon />} onClick={openInstanceDataManager} >Manage variables</Button>
              <Button variant="contained" onClick={saveFormData} disabled={!dataChanged || !instanceRequiredValidation.valid}>Save</Button>
            </div>
          </div>

        </div>
      </PopupShell >
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogText


//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const DataSourceInstance = ({ newTemplate, path, setNewTemplate, variables, setNewVariables, sourcePaths, catalogMap, isManagedPassTemplate, variable, requiredValidation }) => {

  try {
    let templateValue = _.get(newTemplate, path)
    const fixedVariableValue = variable ? `{{${variable}}}` : null

    useEffect(() => {
      if (!fixedVariableValue) return
      if (_.get(newTemplate, path) === fixedVariableValue) return

      setNewTemplate(prev => {
        let nd = { ...prev }
        _.set(nd, path, fixedVariableValue)
        return nd
      })
    }, [fixedVariableValue, newTemplate, path, setNewTemplate])

    if (typeof templateValue !== "undefined") return (
      <SimpleSelection active={Boolean(fixedVariableValue)} newTemplate={newTemplate} setNewTemplate={setNewTemplate} variables={variables} setNewVariables={setNewVariables} sourcePaths={sourcePaths} path={path} catalogMap={catalogMap} isManagedPassTemplate={isManagedPassTemplate} variable={variable} requiredValidation={requiredValidation} />
    )
  }
  catch (err) {
    console.error(err)
  }
}


const SimpleSelection = ({ active, setNewTemplate, newTemplate, variables, setNewVariables, path, sourcePaths, catalogMap, isManagedPassTemplate, variable, requiredValidation }) => {

  const templateValue = _.get(newTemplate, path)
  const variablesValue = dataSelector({ template: newTemplate, variables, path })
  const variablesPath = variable ?? (typeof templateValue === "string" ? (templateValue.match(/^{{(.+)}}$/)?.[1] ?? "") : "")
  const catalogEntry = catalogMap[variablesPath]
  const varSubType = catalogEntry?.var_type
  const instanceValueReadonly = catalogEntry?.editable !== undefined && Number(catalogEntry?.editable) === 0

  const changeHandler = value => {
    setNewTemplate(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)
      return nd
    })
  }

  const changeInstanceValue = value => {
    if (!variablesPath || instanceValueReadonly) return

    setNewVariables(prev => {
      let nd = _.cloneDeep(prev)
      _.set(nd, variablesPath, value)
      return nd
    })
  }

  if (active && typeof variablesValue !== "undefined") return (
    <>
      {variable
        ? (
          <TextField
            label="Variable name"
            value={variable}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={true}
            fullWidth
          />
        )
        : <VariableSelectorSelector updateValue={changeHandler} sourcePaths={sourcePaths} value={templateValue} dataSelector={dataSelector} variables={variables} />}

      {varSubType === "choice" ? (
        <TextField
          select
          label="Variable value"
          value={variablesValue ?? ""}
          slotProps={{ inputLabel: { shrink: true } }}
          disabled={instanceValueReadonly}
          fullWidth
          error={!requiredValidation.valid}
          helperText={requiredValidation.helperText || undefined}
          onChange={e => changeInstanceValue(e.target.value)}
        >
          <MenuItem value="">Select currency</MenuItem>
          {currencies.map(currency => (
            <MenuItem key={currency.code} value={currency.code}>
              {currency.code} - {currency.name}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField label="Variable value"
          value={variablesValue ?? ""}
          slotProps={{ inputLabel: { shrink: true } }}
          type="text"
          disabled={instanceValueReadonly}
          fullWidth
          error={!requiredValidation.valid}
          helperText={requiredValidation.helperText || undefined}
          onChange={e => changeInstanceValue(e.target.value)}
        />
      )}
    </>
  )
}
